import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import FilmMark from "@/components/FilmMark";
import { useLang } from "@/i18n/LanguageContext";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function CarnetListPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = (attempt = 0) => {
    setLoading(true);
    setFailed(false);
    api.get("/blog")
      .then((r) => { setPosts(r.data); setLoading(false); })
      .catch(() => {
        if (attempt < 3) {
          setTimeout(() => load(attempt + 1), 1200 * (attempt + 1));
        } else {
          setLoading(false);
          setFailed(true);
        }
      });
  };

  useEffect(() => { load(); }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <main className="pt-32 pb-24" data-testid="carnet-list">
      <FilmMark />

      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "The Journal · Chronicles of the Thread" : "Le Carnet · Chroniques du Fil"}</div>
        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.88] tracking-tight mt-8">
          {en ? <>The <em className="italic">Journal</em>.</> : <>Le <em className="italic">Carnet</em>.</>}
        </h1>
        <p className="mt-10 font-serif italic text-2xl md:text-3xl leading-[1.35] text-fg/75 max-w-3xl">
          {en ? "A diary of the trail — what we find in the branches, what we name, what we leave behind. The fairies, the encounters, the winter mornings. Articles are written in French." : "Un journal du parcours — ce qu'on trouve dans les branches, ce qu'on baptise, ce qu'on laisse là. Les fées, les rencontres, les matins d'hiver."}
        </p>
      </section>

      {/* Featured */}
      {featured && (
        <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="carnet-featured">
          <Link
            to={`/carnet/${featured.slug}`}
            className="grid md:grid-cols-12 gap-8 md:gap-14 items-center group"
            data-testid={`carnet-item-${featured.slug}`}
          >
            <motion.figure
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="md:col-span-7 overflow-hidden border border-fg/15 aspect-[4/3] bg-fg/5"
            >
              {featured.hero_image ? (
                <img
                  src={featured.hero_image}
                  alt={featured.title}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-fg/40 font-serif italic">Sans image</div>
              )}
            </motion.figure>

            <div className="md:col-span-5">
              <div className="meta-label text-cinabre">§ {en ? "Featured" : "À la une"} · {formatDate(featured.published_at)}</div>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tight">
                {featured.title}
              </h2>
              {featured.subtitle && (
                <p className="mt-4 font-serif italic text-lg md:text-xl leading-[1.5] text-fg/70">{featured.subtitle}</p>
              )}
              <p className="mt-6 text-fg/80 leading-relaxed">{featured.excerpt}</p>
              <div className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre group-hover:gap-3 transition-all">
                {en ? "Read the article" : "Lire l'article"} <ArrowRight size={14} />
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Rest */}
      {rest.length > 0 && (
        <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="carnet-grid">
          <div className="meta-label text-cinabre mb-10">§ {en ? "More chronicles" : "Les autres chroniques"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            {rest.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: i * 0.05 }}
                data-testid={`carnet-item-${p.slug}`}
              >
                <Link to={`/carnet/${p.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden border border-fg/15 bg-fg/5">
                    {p.hero_image && (
                      <img src={p.hero_image} alt={p.title} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]" />
                    )}
                  </div>
                  <div className="mt-5">
                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">
                      {formatDate(p.published_at)}
                    </div>
                    <h3 className="mt-2 font-serif text-2xl md:text-3xl leading-tight tracking-tight group-hover:text-cinabre transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && <p className="mt-3 text-fg/75 leading-relaxed">{p.excerpt}</p>}
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {loading && posts.length === 0 && (
        <section className="mt-24 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="carnet-loading">
          <div className="grid md:grid-cols-12 gap-8 md:gap-14 items-center">
            <div className="md:col-span-7 aspect-[4/3] border border-fg/10 bg-fg/[0.04] animate-pulse" />
            <div className="md:col-span-5 space-y-5">
              <div className="h-3 w-40 bg-fg/[0.08] animate-pulse" />
              <div className="h-12 w-4/5 bg-fg/[0.08] animate-pulse" />
              <div className="h-4 w-full bg-fg/[0.06] animate-pulse" />
              <div className="h-4 w-2/3 bg-fg/[0.06] animate-pulse" />
            </div>
          </div>
          <p className="mt-10 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/45">{en ? "Loading the chronicles…" : "Chargement des chroniques…"}</p>
        </section>
      )}

      {!loading && failed && posts.length === 0 && (
        <section className="mt-24 px-6 md:px-12 max-w-[1000px] mx-auto text-center" data-testid="carnet-error">
          <p className="font-serif italic text-2xl text-fg/60">
            {en ? "The Journal is taking a moment to respond." : "Le Carnet met un peu de temps à répondre."}
          </p>
          <button
            type="button"
            onClick={() => load()}
            className="mt-6 bg-cinabre text-bg px-8 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-fg transition-colors"
            data-testid="carnet-retry"
          >
            {en ? "Try again" : "Réessayer"}
          </button>
        </section>
      )}

      {!loading && !failed && posts.length === 0 && (
        <section className="mt-24 px-6 md:px-12 max-w-[1000px] mx-auto text-center">
          <p className="font-serif italic text-2xl text-fg/60">
            {en ? "No chronicle has been published yet. Come back soon — the first fairy is on its way." : "Aucune chronique n'est encore publiée. Revenez bientôt — la première fée est en chemin."}
          </p>
        </section>
      )}
    </main>
  );
}
